import {
  formatDate,
  notification,
  TokenExpiredNotification,
  USER,
} from '../../assets/constants/strings';
import {
  changePassword,
  confirmEmail,
  createBookmark,
  createComment,
  deleteBookmark,
  deleteBookmarks,
  forgotPassword,
  getUserBookmarks,
  logInFromToken,
  register,
  resendConfirmEmail,
  resetPassword,
  revokeToken,
  tryLogin,
  updateUser,
  inviteUser,
  getUserByClientId,
  deleteUser,
  updateClientRole,
  reinviteUser,
  sendEnquiry,
  getUserClient,
  getUserPlans,
  updateUserUsage,
} from '../../services/axios';
import {
  SET_USER_LOADING,
  LOG_IN,
  FORGOT_PASS,
  RESET_PASS,
  REGISTER,
  CONFIRM_EMAIL,
  RESEND_EMAIL,
  CREATE_BOOKMARK,
  GET_BOOKMARKS,
  SET_BOOKMARK_LOADING,
  DELETE_BOOKMARK,
  DELETE_BOOKMARKS,
  UPDATE_USER,
  UPDATE_USER_ERROR,
  CLEAR_SUBSCRIPTION_ERROR,
  UPDATE_PASS,
  CLEAR_UPDATE_PASS,
  LOG_OUT,
  CREATE_COMMENT,
  INVITE_USER,
  INVITE_USER_ERROR,
  GET_USERS_BY_CLIENT_ID,
  DELETE_USER_CLIENT,
  SET_USER_CLIENT_ERROR,
  UPDATE_CLIENT_ROLE,
  REINVITE_USER_ERROR,
  REINVITE_USER,
  SEND_ENQUIRY,
  SUBSCRIPTION_UPDATE,
} from './userTypes';
import { CLEAN_QUERIES_REDUCER } from '../Revealr/revealrTypes';

export const setLoading = () => ({
  type: SET_USER_LOADING,
});

export const setBookmarkLoading = () => ({
  type: SET_BOOKMARK_LOADING,
});

export const logOut = () => {
  return async (dispatch, getState) => {
    // dispatch(setLoading());

    // call an endpoint
    const res = await revokeToken();

    if (res.succeeded) {
      notification('success', 'Log out Successful!');

      localStorage.setItem('loggedIn', 'false');

      dispatch({
        type: LOG_OUT,
      });

      dispatch({
        type: CLEAN_QUERIES_REDUCER,
      });
    } else {
      notification('info', 'Could not log out.');
    }
  };
};

export const logIn = (userName, password) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading());

      let user = null;
      if (userName === null && password === null) user = await logInFromToken();
      else user = await tryLogin(userName, password);
      // client ID = 0, normal user
      // client ID != 0 && role basic, corporate
      // cliebt ID != 0 && role admin, corporate management
      // client ID = 0 && role super admin, 1WordFlow

      // if (user.email === 'superadmin@1wordflow.info') {
      //   localStorage.setItem('loggedIn', 'true');
      //   dispatch({
      //     type: LOG_IN,
      //     payload: {
      //       userID: user.id,
      //       firstName: user.firstName,
      //       lastName: user.lastName,
      //       displayName: `${user.firstName} ${user.lastName}`,
      //       email: user.email,
      //       organisation: user.organisation,
      //       phoneNumber: user.phoneNumber,
      //       isVerified: true,
      //       isActive: true,
      //       spKey: user.spKey,
      //       token: user.jwToken,
      //       userRole: user.roles ? user.roles : 'Super Admin',
      //       isSuperAdmin: true,
      //     },
      //   });
      // }
      // console.log({ user });
      if (user && !user.reason) {
        // New entitlement model: the .NET auth response now returns `entitlements`
        // (active product lines) and `subscriptions`. The URL decides which portal
        // the user is in; entitlements only decide ACCESS (see services/entitlements.js).
        const entitlements = user.entitlements || [];
        const hasApiEntitlement = entitlements.includes('DeveloperApi');
        const hasReaderEntitlement = entitlements.includes('ReaderApp');

        // API-ONLY accounts skip the Paddle/reader-content computation below —
        // they can never enter the reader portal. Accounts holding BOTH
        // entitlements take the full reader path so their user object carries
        // the reader fields (isActive, userPlan, …) AND the entitlements that
        // unlock the /api portal. Entitlements are the ONLY access signal: the
        // legacy accountType === 'webAPI' fallback is gone, so the subscription
        // backfill MUST have run before this ships to an environment with
        // pre-entitlement webAPI accounts.
        const isApiOnlyAccount = hasApiEntitlement && !hasReaderEntitlement;

        if (isApiOnlyAccount) {
          localStorage.setItem('loggedIn', 'true');

          dispatch({
            type: LOG_IN,
            payload: {
              userID: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              displayName: `${user.firstName} ${user.lastName}`,
              email: user.email,
              organisation: user.organisation,
              orgAdress: user.address,
              orgTaxId: user.orgTaxId,
              phoneNumber: user.phoneNumber,
              jobTitle: user.jobTitle,
              isVerified: true,
              firstTimeLogin: user.firstTimeLogin,
              token: user.jwToken,
              clientId: user.clientId,
              entitlements,
              subscriptions: user.subscriptions || [],
              roles: user.roles || [],
              // Team management (CorporateAdmin) also works from the API portal.
              isAdmin: true,
              isCorporateAdmin: (user.roles || []).some(
                (r) => (r || '').toLowerCase() === 'corporateadmin'
              ),
              activeUserTier: user.activeUserTier,
            },
          });

          return;
        }

        // SuperAdmin internal account (superadmin@1wordflow.info) has no reader client/plan, so the
        // reader computation below would throw. Store a minimal session and let the login page route
        // it to the admin area. Only short-circuits the pure internal admin (no product entitlements);
        // a SuperAdmin who also holds a product subscription takes the normal path.
        const isSuperAdminUser = (user.roles || []).some(
          (r) => (r || '').toLowerCase() === 'superadmin'
        );
        if (isSuperAdminUser && entitlements.length === 0) {
          localStorage.setItem('loggedIn', 'true');
          dispatch({
            type: LOG_IN,
            payload: {
              userID: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              displayName: `${user.firstName} ${user.lastName}`,
              email: user.email,
              isVerified: true,
              firstTimeLogin: user.firstTimeLogin,
              token: user.jwToken,
              clientId: user.clientId,
              entitlements,
              subscriptions: user.subscriptions || [],
              roles: user.roles || [],
            },
          });
          return;
        }

        // Reader path. Plan caps + remaining allowances now come from the .NET auth
        // response (AccountController + UsageService), not Paddle: the backend returns
        // the reader plan's limits alongside the remaining allowances, so we build the
        // same userPlan/userUsage shape the reader screens read — with no Paddle lookup.
        const userPlans = await getUserPlans(user.jwToken);
        const userClient = await getUserClient(user.jwToken, user.clientId);

        // Holding the ReaderApp entitlement means reader access is granted (trial or
        // enterprise). Legacy pre-entitlement accounts (entitlements === []) keep access.
        // Access = holds the ReaderApp entitlement, OR is a genuine pre-entitlement legacy account
        // (no reader subscription at all). An expired trial has a lapsed reader sub but no
        // entitlement, so it must NOT fall through the legacy branch — it's locked out.
        const isActive =
          hasReaderEntitlement ||
          (entitlements.length === 0 && !user.hasReaderSubscription);
        // A reader trial/subscription that has lapsed: had a reader sub, no longer grants access.
        const readerLapsed = Boolean(user.hasReaderSubscription) && !hasReaderEntitlement;
        const isCorporate = user.accountType === 'corporate';

        const readerSubscription = (user.subscriptions || []).find(
          (s) => s.productLine === 'ReaderApp'
        );
        const subscriptionName =
          (readerSubscription && readerSubscription.planName) || 'Free';

        // A cap of 0 means uncapped (the Enterprise reader plan) → shown as "Unlimited";
        // otherwise a numeric cap where used = cap - remaining. Accounts with no plan yet
        // fall back to the Free-tier defaults.
        const planless = !user.planId;
        const capOf = (limit, fallback) =>
          planless ? fallback : limit > 0 ? limit : 'Unlimited';
        // Used allowance for a metric. On a capped plan it's (cap - remaining); on an uncapped
        // Enterprise plan (cap - remaining) is meaningless, so we show the real recorded count
        // (actual) the backend now returns instead of a flat 0.
        const usedOf = (limit, remaining, actual) =>
          planless ? 0 : limit <= 0 ? actual || 0 : Math.max(0, limit - remaining);

        const remainingPageViews = planless ? 100 : user.remainingPageViews;
        const remainingShares = planless ? 10 : user.remainingShares;
        const remainingQueries = planless ? 10 : user.remainingQueries;
        const remainingSummaries = planless ? 10 : user.remainingSummaries;

        const restrictions = {
          [USER.USAGE.PAGEVIEWS]: capOf(user.pageViewLimit, 100),
          [USER.USAGE.SHARES]: capOf(user.shareLimit, 10),
          [USER.USAGE.QUERIES]: capOf(user.queryLimit, 10),
          [USER.USAGE.SUMMARIES]: capOf(user.summaryLimit, 10),
        };

        const userUsage = {
          [USER.USAGE.PAGEVIEWS]: usedOf(
            user.pageViewLimit,
            user.remainingPageViews,
            user.usedPageViews
          ),
          [USER.USAGE.SHARES]: usedOf(
            user.shareLimit,
            user.remainingShares,
            user.usedShares
          ),
          [USER.USAGE.QUERIES]: usedOf(
            user.queryLimit,
            user.remainingQueries,
            user.usedQueries
          ),
          [USER.USAGE.SUMMARIES]: usedOf(
            user.summaryLimit,
            user.remainingSummaries,
            user.usedSummaries
          ),
          [USER.USAGE.ADDITIONALQUERIES]: user.remainingAdditionalQueries,
        };

        const userPlan = {
          subscriptionName,
          startedAt: user.startDate ? formatDate(user.startDate) : '',
          endDate: user.endDate ? formatDate(user.endDate) : '',
          nextBilling: user.endDate ? formatDate(user.endDate) : '',
          restrictions,
          planId: user.planId,
        };

        localStorage.setItem('loggedIn', 'true');

        dispatch({
          type: LOG_IN,
          payload: {
            userID: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            displayName: `${user.firstName} ${user.lastName}`,
            email: user.email,
            organisation: user.organisation,
            phoneNumber: user.phoneNumber,
            jobTitle: user.jobTitle,
            isVerified: true,
            firstTimeLogin: user.firstTimeLogin,
            isActive,
            readerLapsed,
            hasReaderSubscription: Boolean(user.hasReaderSubscription),
            token: user.jwToken,
            clientId: user.clientId,
            entitlements,
            subscriptions: user.subscriptions || [],
            roles: user.roles || [],
            isCorporate,
            isAdmin: true,
            // Real CorporateAdmin role (isAdmin above is hard-coded) — gates the Team page.
            isCorporateAdmin: (user.roles || []).some(
              (r) => (r || '').toLowerCase() === 'corporateadmin'
            ),
            activeUserTier: user.activeUserTier,
            userPlan,
            userUsage,
            userClient,
            userPlans,
            remainingPageViews,
            remainingQueries,
            remainingSummaries,
            remainingShares,
            remainingAdditionalQueries: user.remainingAdditionalQueries,
          },
        });
      } else {
        dispatch({
          type: LOG_IN,
          payload: user,
        });
      }
    } catch (e) {
      console.log('ERROR logging in', e);
      dispatch({
        type: LOG_IN,
        payload: {
          success: false,
          reason: e.message,
          request: LOG_IN,
        },
      });
    }
  };
};

export const clearSubscriptionError = () => async (dispatch) => {
  dispatch({
    type: CLEAR_SUBSCRIPTION_ERROR,
  });
};

export const updatePassword = (passObj) => {
  return async (dispatch, getState) => {
    dispatch(setLoading());

    const token = getState().user.user.token;
    const userID = getState().user.user.userID;

    passObj = {
      id: userID,
      ...passObj,
    };

    const res = await changePassword(passObj, token);

    if (
      res.success === false &&
      res.reason &&
      res.reason.match('The token is expired')
    ) {
      res.reason = 'Session Expired.';
      TokenExpiredNotification();
    }

    dispatch({
      type: UPDATE_PASS,
      payload: res,
    });
  };
};

export const clearUpdatePass = () => async (dispatch) => {
  dispatch({
    type: CLEAR_UPDATE_PASS,
  });
};

export const updateUserInfo = (userObj, isFirstTimeLogin = false) => {
  return async (dispatch, getState) => {
    dispatch(setLoading());

    const token = getState().user.user.token;
    let user = getState().user.user;

    const res = await updateUser(userObj, token);

    if (res.success) {
      if (!isFirstTimeLogin) {
        notification('success', 'Your details were updated!');

        user = {
          ...user,
          firstName: userObj.firstName,
          lastName: userObj.lastName,
          displayName: userObj.firstName + ' ' + userObj.lastName,
          phoneNumber: userObj.phoneNumber,
          organisation: userObj.organisation,
          userClient: {
            ...user.userClient,
            name: userObj.organisation,
          },
        };
      } else {
        user = {
          ...user,
          firstTimeLogin: userObj.firstTimeLogin,
        };
      }

      dispatch({
        type: UPDATE_USER,
        payload: user,
      });
    } else {
      if (res.reason && res.reason.match('The token is expired')) {
        res.reason = 'Session Expired.';
        TokenExpiredNotification();
      } else {
        notification(
          'info',
          'Error',
          `Could update your details. ${
            res.reason
              ? res.reason
              : 'Please try again. If the issue persists, restart the app.'
          }`
        );
      }

      dispatch({
        type: UPDATE_USER_ERROR,
        payload: res,
      });
    }
    return res;
  };
};

export const forgotPass = (email) => {
  return async (dispatch) => {
    dispatch(setLoading());

    // call an endpoint
    const res = await forgotPassword(email);

    dispatch({
      type: FORGOT_PASS,
      payload: res,
    });
  };
};

export const resetPass = (email, token, password, confirmPassword) => {
  return async (dispatch) => {
    dispatch(setLoading());

    // call an endpoint
    const res = await resetPassword(email, token, password, confirmPassword);

    // console.log(res);

    dispatch({
      type: RESET_PASS,
      payload: res,
    });
  };
};

export const registerUser = (user) => {
  return async (dispatch) => {
    dispatch(setLoading());

    // call an endpoint
    const res = await register(user);

    // console.log(res);

    dispatch({
      type: REGISTER,
      payload: res,
    });
  };
};

export const resendEmail = (userId, email) => {
  return async (dispatch) => {
    dispatch(setLoading());

    // call an endpoint
    const res = await resendConfirmEmail(userId, email);

    if (res.success === true) {
      notification('success', 'Email resent! Please check your spam folder.');
    } else {
      notification(
        'info',
        'Error',
        `Could not send email. ${
          res.reason
            ? res.reason
            : 'Please try again. If the issue persists, restart the app.'
        }`
      );
    }

    // setTimeout(() => {
    dispatch({
      type: RESEND_EMAIL,
      payload: res,
    });
    // }, 2500);
  };
};

export const confirmUser = (userId, code) => {
  return async (dispatch) => {
    dispatch(setLoading());

    // call an endpoint
    const res = await confirmEmail(userId, code);

    // console.log(res);

    dispatch({
      type: CONFIRM_EMAIL,
      payload: res,
    });
  };
};

export const getBookmarks = () => {
  return async (dispatch, getState) => {
    if (getState().user.bookmarkLoading === 0) {
      dispatch(setBookmarkLoading());

      const token = getState().user.user.token;
      const userId = getState().user.user.userID;

      // call an endpoint
      const res = await getUserBookmarks(userId, token);

      dispatch({
        type: GET_BOOKMARKS,
        payload: res,
      });
    }
  };
};

export const CreateBookmark = (bookmarkObj) => {
  return async (dispatch, getState) => {
    dispatch(setBookmarkLoading());

    const token = getState().user.user.token;
    const userID = getState().user.user.userID;

    bookmarkObj = {
      ...bookmarkObj,
      userId: userID,
    };

    // call an endpoint
    const res = await createBookmark(bookmarkObj, token);

    let bookmarks = getState().user.bookmarks
      ? [...getState().user.bookmarks]
      : [];

    if (res.success === true) {
      notification('success', 'Bookmark added!', undefined, undefined, 3.5);
      bookmarks.push(res.newBookmark);
    } else {
      if (res.reason && res.reason.match('The token is expired')) {
        res.reason = 'Session Expired.';
        TokenExpiredNotification();
      } else
        notification(
          'info',
          'Error',
          `Could not save bookmark. ${
            res.reason
              ? res.reason
              : 'Please try again. If the issue persists, restart the app.'
          }`
        );
    }

    dispatch({
      type: CREATE_BOOKMARK,
      payload: bookmarks,
    });
  };
};

export const CreateComment = (text) => {
  return async (dispatch, getState) => {
    dispatch(setLoading());

    const userID = getState().user.user.userID;
    const name = getState().user.user ? getState().user.user.firstName : null;

    const commentObj = {
      text: text,
      userId: userID,
    };

    // call an endpoint
    const res = await createComment(commentObj);

    if (res.success === true) {
      notification(
        'success',
        `Thank you for your feedback${name !== null ? ', ' + name : ''}!`,
        'Our team will review your comment and get in touch with you shortly.'
      );
    } else {
      notification(
        'info',
        `Sorry${
          name !== null ? ' ' + name : ''
        }, we could not save comment. Please try again. If the issue persists, restart the app.`
      );
    }

    dispatch({
      type: CREATE_COMMENT,
    });
  };
};

export const DeleteBookmark = (pageId) => {
  return async (dispatch, getState) => {
    dispatch(setBookmarkLoading());

    const token = getState().user.user.token;

    // console.log('Delete', pageId);

    // call an endpoint
    const res = await deleteBookmark(pageId, token);

    let bookmarks = getState().user.bookmarks
      ? [...getState().user.bookmarks]
      : [];

    if (res.success === true) {
      notification('success', 'Bookmark removed!', undefined, undefined, 3.5);
      bookmarks = bookmarks.filter((bookmark) => bookmark.id !== pageId);
    } else {
      if (res.reason && res.reason.match('The token is expired')) {
        res.reason = 'Session Expired.';
        TokenExpiredNotification();
      } else
        notification(
          'info',
          'Error',
          `Could not remove bookmark. ${
            res.reason
              ? res.reason
              : 'Please try again. If the issue persists, restart the app.'
          }`
        );
    }

    dispatch({
      type: DELETE_BOOKMARK,
      payload: bookmarks,
    });
  };
};

export const DeleteBookmarks = (bookmarksList) => {
  return async (dispatch, getState) => {
    dispatch(setBookmarkLoading());

    const token = getState().user.user.token;

    // call an endpoint
    const res = await deleteBookmarks(bookmarksList, token);

    let bookmarks = getState().user.bookmarks
      ? [...getState().user.bookmarks]
      : [];

    if (res.success === true) {
      notification('success', 'Bookmark(s) removed!');

      bookmarksList.forEach((deletedBookmark) => {
        bookmarks = bookmarks.filter(
          (bookmark) => bookmark.id !== deletedBookmark.id
        );
      });
    } else {
      if (res.reason && res.reason.match('The token is expired')) {
        res.reason = 'Session Expired.';
        TokenExpiredNotification();
      } else
        notification(
          'info',
          'Error',
          `Could not remove bookmark(s). ${
            res.reason
              ? res.reason
              : 'Please try again. If the issue persists, restart the app.'
          }`
        );
    }

    dispatch({
      type: DELETE_BOOKMARKS,
      payload: bookmarks,
    });
  };
};

// invite user to corporate account
export const InviteUser = (newUser) => {
  return async (dispatch, getState) => {
    dispatch(setLoading());

    const isAdmin = getState().user.user.isAdmin;
    const token = getState().user.user.token;
    const usersByClient = getState().user.usersByClient
      ? [...getState().user.usersByClient]
      : [];

    getState().user.newUser = newUser;

    // call an endpoint
    const res = await inviteUser(newUser, token);

    if (isAdmin) {
      if (res.success === true) {
        usersByClient.push(res.newUser);

        notification('success', 'User Added');

        dispatch({
          type: INVITE_USER,
          payload: usersByClient,
        });
      } else {
        if (res.reason && res.reason.match('The token is expired')) {
          res.reason = 'Session Expired.';
          TokenExpiredNotification();
        } else if (
          res.reason &&
          !res.reason.includes(
            'Account with this email is already added to your organisation'
          )
        )
          notification(
            'info',
            'Error',
            `Could not add user. ${
              res.reason
                ? res.reason
                : 'Please try again. If the issue persists, restart the app.'
            }`
          );

        dispatch({
          type: INVITE_USER_ERROR,
          payload: res,
        });
      }
    } else {
      notification(
        'info',
        'Error',
        `Could not invite user. ${
          res.reason
            ? res.reason
            : 'Please try again. If the issue persists, restart the app.'
        }`
      );

      dispatch({
        type: INVITE_USER_ERROR,
        payload: res,
      });
    }
  };
};

export const getUsersByClientId = () => {
  return async (dispatch, getState) => {
    dispatch(setLoading());

    const clientId = getState().user.user.clientId;
    const token = getState().user.user.token;
    const isAdmin = getState().user.user.isAdmin;

    // call an endpoint
    const res = await getUserByClientId(clientId, token);

    if (isAdmin && clientId) {
      if (res && res.reason && res.reason.match('The token is expired')) {
        res.reason = 'Session Expired.';
        TokenExpiredNotification();
      }

      // console.log(res, clientId);

      dispatch({
        type: GET_USERS_BY_CLIENT_ID,
        payload: res,
      });
    } else {
      dispatch({
        type: SET_USER_CLIENT_ERROR,
        payload: res,
      });
    }
  };
};

export const DeleteUser = (userId) => {
  return async (dispatch, getState) => {
    dispatch(setLoading());

    const token = getState().user.user.token;
    const clientId = getState().user.user.clientId;
    const isAdmin = getState().user.user.isAdmin;

    // let usersByClient = getState().user.usersByClient ? [...getState().user.usersByClient] : [];

    // console.log('Delete', userId);

    // call an endpoint
    const res = await deleteUser(clientId, userId, token);

    const clientUsers = await getUserByClientId(clientId, token);

    if (isAdmin) {
      if (res.success === true) {
        // usersByClient = usersByClient.filter((user) => user.id !== userId);

        notification('success', 'The user has been removed from your account.');

        dispatch({
          type: DELETE_USER_CLIENT,
          payload: clientUsers,
        });
      } else {
        if (res.reason && res.reason.match('The token is expired')) {
          res.reason = 'Session Expired.';
          TokenExpiredNotification();
        } else
          notification(
            'info',
            'Error',
            `Could not remove user. ${
              res.reason
                ? res.reason
                : 'Please try again. If the issue persists, restart the app.'
            }`
          );

        dispatch({
          type: SET_USER_CLIENT_ERROR,
          payload: res,
        });
      }
    } else {
      notification(
        'info',
        'Error',
        `Could not remove user. ${
          res.reason
            ? res.reason
            : 'Please try again. If the issue persists, restart the app.'
        }`
      );

      dispatch({
        type: SET_USER_CLIENT_ERROR,
        payload: [],
      });
    }
  };
};

export const UpdateClientRole = (data) => {
  return async (dispatch, getState) => {
    dispatch(setLoading());

    const token = getState().user.user.token;
    const isAdmin = getState().user.user.isAdmin;

    // call an endpoint
    const res = await updateClientRole(data, token);

    // console.log('res UPDATE role', res);

    if (isAdmin) {
      if (res.success === true) {
        notification('success', 'User role updated.');

        dispatch({
          type: UPDATE_CLIENT_ROLE,
        });
      } else {
        if (res.reason && res.reason.match('The token is expired')) {
          res.reason = 'Session Expired.';
          TokenExpiredNotification();
        } else
          notification(
            'info',
            'Error',
            `Could not update user role. ${
              res.reason
                ? res.reason
                : 'Please try again. If the issue persists, restart the app.'
            }`
          );

        dispatch({
          type: SET_USER_CLIENT_ERROR,
        });
      }
    } else {
      notification(
        'info',
        'Error',
        `Could not update user role. ${res.reason}. Please try again. If the issue persists, restart the app.`
      );

      dispatch({
        type: SET_USER_CLIENT_ERROR,
      });
    }
  };
};

// reinvite inactive (already exists but user invited account status = inactive) to corporate account
export const ReinviteUser = (email, clientId) => {
  return async (dispatch, getState) => {
    dispatch(setLoading());

    const token = getState().user.user.token;
    const isAdmin = getState().user.user.isAdmin;

    // call an endpoint
    const res = await reinviteUser(email, clientId, token);
    // call an endpoint
    const clientUsers = await getUserByClientId(clientId, token);

    if (isAdmin) {
      if (res.success === true) {
        notification('success', 'Account reactivated.');

        dispatch({
          type: REINVITE_USER,
          payload: clientUsers,
        });
      } else {
        console.log(res);
        notification('info', 'Error', 'Could not reactivate account.');

        dispatch({
          type: REINVITE_USER_ERROR,
          payload: res,
        });
      }
    } else {
      notification('info', 'Error', 'Could not reactivate account.');

      dispatch({
        type: REINVITE_USER_ERROR,
        payload: res,
      });
    }
  };
};

export const sendQuote = (quote) => {
  return async (dispatch) => {
    dispatch(setLoading());

    // call an endpoint
    const res = await sendEnquiry(quote);

    // console.log(res);

    if (res.success === true) {
      dispatch({
        type: SEND_ENQUIRY,
        payload: res,
      });
    } else {
      notification(
        'info',
        'Error',
        'Could not sent enquiry. Please, try again.'
      );
      dispatch({
        type: SEND_ENQUIRY,
        payload: res,
      });
    }
  };
};

export const cleanSubscription = () => {
  return async (dispatch, getState) => {
    dispatch({
      type: SUBSCRIPTION_UPDATE,
      payload: null,
    });
  };
};

export const cleanEnquiry = () => {
  return async (dispatch, getState) => {
    dispatch({
      type: SEND_ENQUIRY,
      payload: null,
    });
  };
};

// Reader usage tracking (NOT billing): record one feature use against /v1/Usage and
// optimistically adjust the in-memory allowances so gating updates without a reload.
// Relocated here from the retired Paddle store — it always dispatched the user reducer.
export const UpdateUserUsage = (type) => {
  return async (dispatch, getState) => {
    let user = getState().user.user;

    const data = {
      userId: user.userID,
      clientId: user.userClient.id,
      featureUsed: type,
      planId: user.userClient.planId,
    };
    // update user usage database
    const res = await updateUserUsage(data, user.token);

    let remainingPageViews = user.remainingPageViews;
    let remainingQueries = user.remainingQueries;
    let remainingSummaries = user.remainingSummaries;
    let remainingShares = user.remainingShares;
    let remainingAdditionalQueries = user.remainingAdditionalQueries;

    if (type === USER.USAGE.PAGEVIEWS)
      remainingPageViews = user.remainingPageViews - 1;
    else if (type === USER.USAGE.QUERIES)
      remainingQueries = user.remainingQueries - 1;
    else if (type === USER.USAGE.SUMMARIES)
      remainingSummaries = user.remainingSummaries - 1;
    else if (type === USER.USAGE.SHARES)
      remainingShares = user.remainingShares - 1;
    else if (type === USER.USAGE.ADDITIONALQUERIES)
      remainingAdditionalQueries = user.remainingAdditionalQueries - 1;

    if (res) {
      user = {
        ...user,
        remainingPageViews: remainingPageViews,
        remainingQueries: remainingQueries,
        remainingSummaries: remainingSummaries,
        remainingShares: remainingShares,
        remainingAdditionalQueries: remainingAdditionalQueries,
        userUsage: {
          ...user.userUsage,
          [type]: user.userUsage[type] + 1,
        },
      };

      dispatch({
        type: UPDATE_USER,
        payload: user,
      });
    }
  };
};

/* ------------------------------------------------------------------ *
 * TEMPORARY STUBS — Reg City Web API portal card management
 * ------------------------------------------------------------------
 * The API portal billing components (API/AccountSettings/Payments,
 * API/UserAccount/Payments) were written against the old Stripe-era
 * card actions, which were removed when the main app migrated to
 * Paddle. These no-op stubs exist only so the bundle compiles and the
 * rest of the app/portal works. They do NOT manage cards.
 *
 * TODO: wire these to Paddle's payment-method APIs (or remove the
 * portal card-management UI) before enabling billing in the portal.
 * ------------------------------------------------------------------ */
const WEB_API_CARDS_NOT_IMPLEMENTED =
  'Card management is not available yet.';

export const addCard = () => async () => {
  console.warn('[stub] addCard called — not implemented (Paddle migration pending)');
  notification('info', 'Coming soon', WEB_API_CARDS_NOT_IMPLEMENTED);
};

export const UpdateCard = () => async () => {
  console.warn('[stub] UpdateCard called — not implemented (Paddle migration pending)');
  notification('info', 'Coming soon', WEB_API_CARDS_NOT_IMPLEMENTED);
};

export const UpdateDefaultCard = () => async () => {
  console.warn('[stub] UpdateDefaultCard called — not implemented (Paddle migration pending)');
  notification('info', 'Coming soon', WEB_API_CARDS_NOT_IMPLEMENTED);
};

export const DeleteCard = () => async () => {
  console.warn('[stub] DeleteCard called — not implemented (Paddle migration pending)');
  notification('info', 'Coming soon', WEB_API_CARDS_NOT_IMPLEMENTED);
};
